import React, { createContext, useContext, useEffect, useState } from 'react'
import type { AuthUser } from './api/auth'
import { fetchCurrentUser, loginUser, logoutUser } from './api/auth'

interface AuthContextValue {
  user: AuthUser | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // Initial load: try to restore session from cookie-backed token
  useEffect(() => {
    let cancelled = false

    const init = async () => {
      setIsLoading(true)
      try {
        const result = await fetchCurrentUser()
        if (!cancelled) {
          setUser(result.user)
        }
      } catch (err) {
        console.error('Failed to restore auth session', err)
        if (!cancelled) {
          setUser(null)
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    void init()

    return () => {
      cancelled = true
    }
  }, [])

  const login = async (email: string, password: string) => {
    const result = await loginUser(email, password)
    setUser(result.user)
  }

  const logout = async () => {
    await logoutUser()
    setUser(null)
  }

  const refreshUser = async () => {
    try {
      const result = await fetchCurrentUser()
      setUser(result.user)
    } catch (err) {
      console.error('Failed to refresh user', err)
    }
  }

  const value: AuthContextValue = {
    user,
    isLoading,
    login,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}


